// The Breeze panel's state: the shared notes, questions, and next steps the
// whole room sees, and the private notes one person keeps. Fed by the call
// store, which hands it every room message and a way to send.
//
// Private data has two copies on purpose: the Room holds it under a key only
// this browser (or this account) can present, and a mirror here lets a reload
// show it at once. `dirty` is the one rule that keeps them from fighting: an
// edit not yet acknowledged by the Room is never overwritten by what the Room
// answers, it is pushed instead.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  emptyPanel,
  emptyPrivate,
  isEmptyPrivate,
  PANEL_LIMITS,
  sanitizePrivate,
  type ClientMessage,
  type PanelItem,
  type PanelList,
  type PanelState,
  type PrivateData,
  type PrivateQuestion,
  type RoomMessage,
  type SuggestionAudience,
  type SuggestionItem,
} from "@breeze/core";
import {
  getPanelLocal,
  getPanelToken,
  setPanelLocal,
  setPanelPrev,
} from "../lib/storage";

interface LocalCopy {
  readonly data: PrivateData;
  readonly dirty: boolean;
  /** The call this copy belongs to (epoch ms), null when written before one. */
  readonly call: number | null;
}

const SAVE_DELAY_MS = 600;
const ERROR_MS = 4000;

function parseCopy(json: string | null): LocalCopy {
  if (!json) return { data: emptyPrivate(), dirty: false, call: null };
  try {
    const raw = JSON.parse(json) as { data?: unknown; dirty?: unknown; call?: unknown };
    const data = sanitizePrivate(raw.data) ?? emptyPrivate();
    return { data, dirty: raw.dirty === true, call: typeof raw.call === "number" ? raw.call : null };
  } catch {
    return { data: emptyPrivate(), dirty: false, call: null };
  }
}

function newId(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export interface SuggestionBatch {
  readonly audience: SuggestionAudience;
  readonly items: readonly SuggestionItem[];
}

export interface ItemPatch {
  readonly text?: string;
  readonly owner?: string;
  readonly done?: boolean;
  readonly asked?: boolean;
}

export interface PanelStore {
  /** The shared lists, or null until the Room has sent them. */
  readonly shared: PanelState | null;
  readonly priv: PrivateData;
  /** Notes kept from an earlier call in this room, offered once. */
  /** Phase B: everything the room sent as `suggestions`. Stored, not shown. */
  readonly suggestions: readonly SuggestionBatch[];
  /** A refusal worth saying (a rate limit, a full list), for a few seconds. */
  readonly problem: string | null;
  /** Whether the private notes are stored in the room yet. */
  readonly saved: boolean;
  readonly addItem: (list: PanelList, text: string, extra?: { owner?: string }) => void;
  readonly editItem: (list: PanelList, id: string, patch: ItemPatch) => void;
  readonly removeItem: (list: PanelList, id: string) => void;
  readonly setGoal: (text: string) => void;
  readonly setNotes: (text: string) => void;
  readonly addQuestion: (text: string, by?: "breeze") => void;
  readonly updateQuestion: (id: string, patch: Partial<Pick<PrivateQuestion, "text" | "done">>) => void;
  readonly removeQuestion: (id: string) => void;
  /** Post the goal as a shared note once let in (the lobby checkbox). */
  readonly setShareGoal: (on: boolean) => void;
  /** Ask the Room for this person's private notes (on connect and on let-in). */
  readonly requestPrivate: () => void;
  /** Handle a room message. True when it was a panel message. */
  readonly ingest: (message: RoomMessage) => boolean;
}

export function usePanel(roomKey: string, send: (message: ClientMessage) => void): PanelStore {
  const [shared, setShared] = useState<PanelState | null>(null);
  const localRef = useRef<LocalCopy>(parseCopy(getPanelLocal(roomKey)));
  const [priv, setPriv] = useState<PrivateData>(localRef.current.data);
  const [suggestions, setSuggestions] = useState<readonly SuggestionBatch[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(!localRef.current.dirty);
  const problemTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editSeq = useRef(0);
  const sentSeq = useRef(0);
  const shareGoalRef = useRef(false);
  const sendRef = useRef(send);
  sendRef.current = send;

  const persistLocal = useCallback(
    (next: LocalCopy) => {
      localRef.current = next;
      setPanelLocal(roomKey, JSON.stringify(next));
    },
    [roomKey],
  );

  const flush = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    if (!localRef.current.dirty) return;
    sentSeq.current = editSeq.current;
    sendRef.current({ type: "privateSet", token: getPanelToken(), data: localRef.current.data });
  }, []);

  const commit = useCallback(
    (data: PrivateData) => {
      persistLocal({ ...localRef.current, data, dirty: true });
      editSeq.current += 1;
      setPriv(data);
      setSaved(false);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(flush, SAVE_DELAY_MS);
    },
    [flush, persistLocal],
  );

  // An edit still waiting on its timer goes out when the page is hidden or closed.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onHide);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (problemTimer.current) clearTimeout(problemTimer.current);
    };
  }, [flush]);

  const say = useCallback((text: string) => {
    setProblem(text);
    if (problemTimer.current) clearTimeout(problemTimer.current);
    problemTimer.current = setTimeout(() => setProblem(null), ERROR_MS);
  }, []);

  const requestPrivate = useCallback(() => {
    sendRef.current({ type: "privateGet", token: getPanelToken() });
  }, []);

  const reconcile = useCallback(
    (data: PrivateData | null, call: number | null) => {
      let local = localRef.current;
      // A copy stamped with a different call than the one the Room is in (or
      // with none live) is from a call that has ended. It is set aside, not
      // pushed into this one.
      if (local.call !== null && local.call !== call) {
        if (!isEmptyPrivate(local.data)) {
          // Kept on the device so nothing is lost; no longer shown back to him
          // (Adam, 2026-10-10: no "notes from your last call here" card).
          setPanelPrev(roomKey, JSON.stringify(local.data));
        }
        local = { data: emptyPrivate(), dirty: false, call };
        persistLocal(local);
        setPriv(local.data);
        setSaved(true);
      } else if (local.call === null && call !== null) {
        local = { ...local, call };
        persistLocal(local);
      }
      if (local.dirty) {
        flush();
        return;
      }
      if (data) {
        persistLocal({ ...local, data });
        setPriv(data);
        setSaved(true);
      } else if (!isEmptyPrivate(local.data)) {
        // The Room holds nothing for this key but this browser has notes
        // (a reload after the Room cleared, or notes written before the call).
        persistLocal({ ...local, dirty: true });
        editSeq.current += 1;
        flush();
      }
    },
    [flush, persistLocal, roomKey],
  );

  const ingest = useCallback(
    (message: RoomMessage): boolean => {
      switch (message.type) {
        case "panelState": {
          setShared(message.panel);
          if (shareGoalRef.current) {
            shareGoalRef.current = false;
            const goal = localRef.current.data.goal.trim().slice(0, PANEL_LIMITS.itemChars);
            if (goal) sendRef.current({ type: "panelAdd", list: "notes", text: goal, goal: true });
          }
          return true;
        }
        case "panelItem": {
          setShared((cur) => {
            const base = cur ?? emptyPanel();
            const list = base[message.list];
            const at = list.findIndex((i) => i.id === message.item.id);
            const next: readonly PanelItem[] =
              at >= 0 ? list.map((i, n) => (n === at ? message.item : i)) : [...list, message.item];
            return { ...base, [message.list]: next };
          });
          return true;
        }
        case "panelRemoved": {
          setShared((cur) => (cur ? { ...cur, [message.list]: cur[message.list].filter((i) => i.id !== message.id) } : cur));
          return true;
        }
        case "privateState":
          reconcile(message.data, message.call);
          return true;
        case "privateSaved":
          if (sentSeq.current === editSeq.current && localRef.current.dirty) {
            persistLocal({ ...localRef.current, dirty: false });
            setSaved(true);
          }
          return true;
        case "suggestions":
          setSuggestions((cur) => [...cur, { audience: message.audience, items: message.items }]);
          return true;
        case "error":
          if (message.code === "panel") {
            say("That did not go through. Try again in a moment.");
            return true;
          }
          if (message.code === "private") {
            say(message.message);
            return true;
          }
          return false;
        default:
          return false;
      }
    },
    [persistLocal, reconcile, say],
  );

  const addItem = useCallback((list: PanelList, text: string, extra?: { owner?: string }) => {
    const clean = text.trim();
    if (!clean) return;
    sendRef.current({ type: "panelAdd", list, text: clean, ...(extra?.owner?.trim() ? { owner: extra.owner.trim() } : {}) });
  }, []);

  const editItem = useCallback((list: PanelList, id: string, patch: ItemPatch) => {
    sendRef.current({ type: "panelEdit", list, id, ...patch });
  }, []);

  const removeItem = useCallback((list: PanelList, id: string) => {
    sendRef.current({ type: "panelDelete", list, id });
  }, []);

  const current = () => localRef.current.data;

  const setGoal = useCallback((text: string) => commit({ ...current(), goal: text }), [commit]);
  const setNotes = useCallback((text: string) => commit({ ...current(), notes: text }), [commit]);
  const addQuestion = useCallback(
    (text: string, by?: "breeze") => {
      const clean = text.trim().slice(0, PANEL_LIMITS.itemChars);
      if (!clean || current().questions.length >= PANEL_LIMITS.privateQuestions) return;
      const q: PrivateQuestion = { id: newId(), text: clean, done: false, ...(by ? { by } : {}) };
      commit({ ...current(), questions: [...current().questions, q] });
    },
    [commit],
  );
  const updateQuestion = useCallback(
    (id: string, patch: Partial<Pick<PrivateQuestion, "text" | "done">>) => {
      commit({ ...current(), questions: current().questions.map((q) => (q.id === id ? { ...q, ...patch } : q)) });
    },
    [commit],
  );
  const removeQuestion = useCallback(
    (id: string) => commit({ ...current(), questions: current().questions.filter((q) => q.id !== id) }),
    [commit],
  );

  const setShareGoal = useCallback((on: boolean) => {
    shareGoalRef.current = on;
  }, []);

  return {
    shared,
    priv,
    suggestions,
    problem,
    saved,
    addItem,
    editItem,
    removeItem,
    setGoal,
    setNotes,
    addQuestion,
    updateQuestion,
    removeQuestion,
    setShareGoal,
    requestPrivate,
    ingest,
  };
}
